<template>
  <FormPto
    v-model="modelFormData"
    ref="FormPto"
    :is-edit="isEdit"
    :is-sending="isSending"
    :clear-on-destroy="!isEdit"
    @request="onSubmit"
    @cancel="onCancel"
  >
    <PTOProjectTitle v-if="isEdit" is-edit />

    <VInput
      v-if="!isEdit"
      label="Наименование проекта"
      name="name"
      :value="project"
      disabled
    />

    <VInput
      label="Смета"
      name="estimate"
      required
    >
      <Validation for="required">Поле обязательно</Validation>
    </VInput>

    <VSelect
      ref="SelectBaseType"
      label="Тип основания"
      name="base_type"
      :items="estimateBaseTypeListMapped"
      @change="changeBaseType"
    />

    <VSelect
      ref="SelectBase"
      label="Основание"
      name="base"
      :items="baseOptions"
      :handler-search="baseSearchHandler"
      :item-text="baseItemText"
      :required="hasType"
      search
    >
      <Validation for="required">Поле обязательно</Validation>
    </VSelect>

    <VSelect
      label="МОЛ"
      name="mol"
      :items="userList"
      :handler-search="GETUserList"
      search
      userSelect
    />

    <AppInputDate
      title="Дата получения сметы"
      name="receive_date"
      type="single"
    />

    <VInput
      label="Стоимость (в ценах 2001)"
      name="price"
      required
      pattern="^\d*(?:[.]\d{0,3})?$"
    >
      <Validation for="required">Поле обязательно</Validation>
      <Validation for="pattern">Формат ввода: "1000.001"</Validation>
    </VInput>

    <VInput
      label="Поставка заказчика"
      name="customer_delivery"
      required
      pattern="^\d*(?:[.]\d{0,3})?$"
    >
      <Validation for="required">Поле обязательно</Validation>
      <Validation for="pattern">Формат ввода: "1000.001"</Validation>
    </VInput>

    <VInput
      label="Индекс"
      name="index"
      required
      pattern="^\d*(?:[.]\d{0,3})?$"
    >
      <Validation for="required">Поле обязательно</Validation>
      <Validation for="pattern">Формат ввода: "1000.001"</Validation>
    </VInput>

    <VInput
      label="Трудозатраты"
      name="labor_costs"
      pattern="^\d{0,2}(?:[.]\d{0,2})?$"
    >
      <Validation for="pattern">Формат ввода: "10.01"</Validation>
    </VInput>
  </FormPto>
</template>

<script>
import AppInputDate from '@/components/page/input/AppInputDate'
import FormPto from '@/components/pto/form/FormPto'
import PTOProjectTitle from '@/components/pto/title/PTOProjectTitle'
import Validation from '@/components/page/Form/Validation.vue'
import VInput from '@/components/page/Form/Vinput/Vinput.vue'
import VSelect from '@/components/page/Form/VSelect.vue'

import Vue from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { MIXIN_FORM_PTO } from '@/mixins/module/pto/mixinFormPto'
import { MIXIN_USER_LIST } from '@/mixins/handbook/userList'

import { prepareProjectEstimateFormData } from '@/utils/page/pto/project/estimate'
import { EPtoHandbookEstimateBaseTypeName } from '@/api/pto'
import { HHMM } from '@/utils/form/regularExpression'

export default {
  name: 'FormPtoProjectEstimate',
  components: {
    AppInputDate,
    FormPto,
    PTOProjectTitle,
    Validation,
    VInput,
    VSelect
  },

  mixins: [
    MIXIN_FORM_PTO,
    MIXIN_USER_LIST
  ],

  data: () => ({
    baseTypeSelectedName: null,
    hasTypeBase: false,
    regularHHMM: HHMM
  }),

  created () {
    const baseType = this.formData?.base_type
    if (baseType) {
      this.setBaseTypeSelectedName(baseType.name)
    }

    Promise.all([
      this.GETChangeListByProjectId(),
      this.GETDocumentListByProjectId(),
      this.GETStatementListByProjectId()
    ])
  },

  computed: {
    ...mapState('handbook/pto', {
      changeList: 'changeList',
      documentList: 'documentList',
      statementList: 'statementList'
    }),
    ...mapGetters('pto/project', {
      currentProject: 'getCurrentItem'
    }),
    ...mapGetters('handbook/pto', {
      estimateBaseTypeListMapped: 'estimateBaseTypeListMapped'
    }),
    project () {
      return this.currentProject.name
    },
    baseOptions () {
      switch (this.baseTypeSelectedName) {
        case EPtoHandbookEstimateBaseTypeName.change: return this.changeList
        case EPtoHandbookEstimateBaseTypeName.document: return this.documentList
        case EPtoHandbookEstimateBaseTypeName.statement: return this.statementList
        default: return []
      }
    },
    baseSearchHandler () {
      switch (this.baseTypeSelectedName) {
        case EPtoHandbookEstimateBaseTypeName.change: return this.GETChangeListByProjectId
        case EPtoHandbookEstimateBaseTypeName.document: return this.GETDocumentListByProjectId
        case EPtoHandbookEstimateBaseTypeName.statement: return this.GETStatementListByProjectId
        default: return undefined
      }
    },
    baseItemText () {
      switch (this.baseTypeSelectedName) {
        case EPtoHandbookEstimateBaseTypeName.change: return 'number'
        case EPtoHandbookEstimateBaseTypeName.statement: return 'number'
        default: return 'name'
      }
    },
    hasType () {
      return this.hasTypeBase
    }
  },

  methods: {
    ...mapActions('handbook/pto', {
      GETChangeList: 'GETChangeList',
      GETDocumentList: 'GETDocumentList',
      GETStatementList: 'GETStatementList'
    }),
    onSubmit (data) {
      this.$emit('request', prepareProjectEstimateFormData(data))
    },

    GETChangeListByProjectId ({ search = null } = {}) {
      return this.GETChangeList({
        projectId: this.currentProject.id,
        search
      })
    },

    GETDocumentListByProjectId ({ search = null } = {}) {
      return this.GETDocumentList({
        projectId: this.currentProject.id,
        search
      })
    },

    GETStatementListByProjectId ({ search = null } = {}) {
      return this.GETStatementList({
        projectId: this.currentProject.id,
        search
      })
    },

    changeBaseType (value) {
      Vue.set(this.modelFormData, 'base', null)
      this.resetSelectBase()

      if (!value) {
        this.hasTypeBase = false
        return this.setBaseTypeSelectedName(null)
      }

      this.hasTypeBase = true
      this.setBaseTypeSelectedName(value.name)
    },

    resetSelectBase () {
      this.$refs.SelectBase.inputValue = null
    },

    setBaseTypeSelectedName (name) {
      this.baseTypeSelectedName = name
    }
  }
}
</script>
